import { useState, useEffect } from 'react';

import { RunningStats, StravaAthlete } from '../types/strava';
import { downloadDataFromS3 } from '../services/s3Service';

const isS3Configured = (): boolean => {
  return !!(process.env.REACT_APP_AWS_REGION && process.env.REACT_APP_S3_BUCKET_NAME);
};

export interface UseS3RunningStatsReturn {
  isLoading: boolean;
  runningStats: RunningStats | null;
  athleteInfo: StravaAthlete | null;
  error: string | null;
  refresh: () => void;
}

export const useS3RunningStats = (fileName: string): UseS3RunningStatsReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [runningStats, setRunningStats] = useState<RunningStats | null>(null);
  const [athleteInfo, setAthleteInfo] = useState<StravaAthlete | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDataFromS3 = async () => {
      if (!isS3Configured()) {
        setError('S3 service is not configured properly');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const data = await downloadDataFromS3(fileName);

        if (data.runningStats) {
          setRunningStats(data.runningStats);
        }

        if (data.athleteInfo) {
          setAthleteInfo(data.athleteInfo);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load data from S3';
        setError(errorMessage);
        console.error('Error loading data from S3:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadDataFromS3();
  }, [fileName]);

  const refresh = () => {
    if (!isS3Configured()) {
      setError('S3 service is not configured properly');
      return;
    }

    const loadDataFromS3 = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const data = await downloadDataFromS3(fileName);

        if (data.runningStats) {
          setRunningStats(data.runningStats);
        }

        if (data.athleteInfo) {
          setAthleteInfo(data.athleteInfo);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load data from S3';
        setError(errorMessage);
        console.error('Error loading data from S3:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadDataFromS3();
  };

  return {
    isLoading,
    runningStats,
    athleteInfo,
    error,
    refresh,
  };
};
